import {
  BenefitDependentTypeEnum,
  BenefitTypeEnum,
  ExternalPlanData,
  HealthPlanMetalLevelEnum,
  MarketplacePlanDTO,
  MarketplacePlanLevelEnum,
  PendingCompanyBenefitEnrollmentDTO,
  SexEnum
} from "@vitable/asclepius-api-client-ts"
import {
  BenefitElectionDependent,
  NonVpcBenefitElection,
  NonVpcBenefitPlanSelection,
  VpcBenefitElection
} from "@/modules/enrollment-new/enrollment-flow-types"
import { displayBenefitName } from "@common/utilities/benefit-utils"
import { asDateTime } from "@common/utilities/time-utils"

export const MEDICARE_ELIGIBILITY_AGE = 65

export function isMedicareEligible(member: { age: number } | null | undefined): boolean {
  return (member?.age ?? 0) >= MEDICARE_ELIGIBILITY_AGE
}

/** Reads `employerAllowanceInCents` from any selection variant that carries it; 0 otherwise. */
export function getEmployerAllowanceCents(selection: NonVpcBenefitPlanSelection): number {
  if (selection?.type === "pendingPlanShopping" || selection?.type === "external" || selection?.type === "medicare") {
    return selection.employerAllowanceInCents ?? 0
  }
  return 0
}

/** Final decision reached: picked internal/external/medicare, or explicitly waived. Pending plan-shopping does NOT count. */
export function isPlanSelected(election: { selection: NonVpcBenefitPlanSelection }): boolean {
  if (election.selection === null) return true
  const t = election.selection?.type
  return t === "internal" || t === "external" || t === "medicare"
}

/** External plan picked, Medicare confirmed, or explicit waive — shopping leg is done either way. */
export function isPlanShoppingResolved(election: { selection: NonVpcBenefitPlanSelection }): boolean {
  if (election.selection === null) return true
  const t = election.selection?.type
  return t === "external" || t === "medicare"
}

/** An external plan is already picked and carrier questions are on, so the member's answers live on the questions step. */
export function canResumeCarrierQuestions(election: NonVpcBenefitElection | undefined): boolean {
  return Boolean(election?.enrollment.carrier_questions_enabled && election.selection?.type === "external")
}

export function selectBenefitElectionLabel(election: NonVpcBenefitElection): string {
  if (election.enrollment.benefit_type === BenefitTypeEnum.Medical) {
    if (election.selection?.type === "internal") return election.selection.benefitPlan.name
    if (election.selection?.type === "external") return election.selection.externalPlan.name
  }
  return displayBenefitName(election.enrollment)
}

export function hasIchraEnrollment(
  elections: ReadonlyArray<{ enrollment: { requires_external_plan_in: boolean } }>
): boolean {
  return elections.some((e) => e.enrollment.requires_external_plan_in)
}

export function sexAtBirthOptions(hasIchra: boolean): SexEnum[] {
  return hasIchra ? [SexEnum.Male, SexEnum.Female] : Object.values(SexEnum)
}

export function getDependentFullName(
  dep: Pick<BenefitElectionDependent, "firstName" | "lastName"> | undefined
): string | undefined {
  if (!dep) return undefined
  const name = `${dep.firstName ?? ""} ${dep.lastName ?? ""}`.trim()
  return name || undefined
}

type ChildDependentAgeLimit = Pick<
  PendingCompanyBenefitEnrollmentDTO,
  "dependent_eligibility_date" | "max_child_dependent_age"
>

/** Mirrors the backend rule: a child who has reached the limit on their coverage start date can't be enrolled. */
export function isChildDependentOverAgeLimit(
  dependent: Pick<BenefitElectionDependent, "type" | "dateOfBirth">,
  enrollment: ChildDependentAgeLimit
): boolean {
  const eligibilityDate = enrollment.dependent_eligibility_date
  const maxAge = enrollment.max_child_dependent_age
  if (dependent.type !== BenefitDependentTypeEnum.Child || !eligibilityDate || maxAge === null) return false

  const dateOfBirth = asDateTime(dependent.dateOfBirth)
  const reference = asDateTime(eligibilityDate)
  // Mirrors backend calculate_age_in_years; a Luxon diff would count a Feb 29 birthday as a full year on Feb 28.
  const birthdayPending =
    reference.month < dateOfBirth.month || (reference.month === dateOfBirth.month && reference.day < dateOfBirth.day)
  const ageOnEligibilityDate = reference.year - dateOfBirth.year - (birthdayPending ? 1 : 0)
  return ageOnEligibilityDate >= maxAge
}

/** The member's waived dependents plus children too old to enroll, without changing what the member chose. */
export function getEffectiveWaivedDependentsIds(
  election: { enrollment: ChildDependentAgeLimit; waivedDependentsIds: Array<string> },
  dependents: ReadonlyArray<BenefitElectionDependent>
): Array<string> {
  const ineligibleIds = dependents
    .filter((d) => isChildDependentOverAgeLimit(d, election.enrollment))
    .map((d) => d.legalDependentId)
  return [...new Set([...election.waivedDependentsIds, ...ineligibleIds])]
}

export function getVpcBenefitElectionFromPendingEnrollment(
  enrollment: PendingCompanyBenefitEnrollmentDTO
): Optional<VpcBenefitElection> {
  // ASSUMPTION 1: Any VPC Benefit will have Vitable Only flag (supports VPC + MEC case)
  // ASSUMPTION 2: All VPC Benefits will have only 1 tier cost
  // ASSUMPTION 3: A company can only offer 1 VPC Benefit at a time
  for (const possibleVpcBenefitPlan of enrollment.benefit_plans) {
    if (possibleVpcBenefitPlan.vitable_only_in) {
      const theOnlyVpcTierThatExists = possibleVpcBenefitPlan!.tier_costs[0]
      return {
        enrollment: enrollment,
        vpcBenefitPlan: possibleVpcBenefitPlan,
        vpcTierCost: theOnlyVpcTierThatExists,
        waived: false,
        waivedDependentsIds: []
      }
    }
  }
  return undefined
}

export function convertMarketplacePlanDTOToExternalPlanData(
  plan: MarketplacePlanDTO,
  coverageStartDate: string
): ExternalPlanData {
  const healthPlanEnumConverter: Record<MarketplacePlanLevelEnum, HealthPlanMetalLevelEnum> = {
    bronze: HealthPlanMetalLevelEnum.Bronze,
    silver: HealthPlanMetalLevelEnum.Silver,
    gold: HealthPlanMetalLevelEnum.Gold,
    platinum: HealthPlanMetalLevelEnum.Platinum,
    catastrophic: HealthPlanMetalLevelEnum.Bronze, // TODO: is this correct?
    expanded_bronze: HealthPlanMetalLevelEnum.Bronze // TODO: is this correct?
  }

  return {
    ideon_plan_id: plan.id,
    name: plan.name,
    carrier_name: plan.carrier_name,
    plan_type: plan.plan_type,
    level: healthPlanEnumConverter[plan.level],
    premium_in_cents: plan.premium_in_cents,
    individual_medical_deductible_in_network_in_cents: plan.individual_medical_deductible_in_network_in_cents,
    family_medical_deductible_in_network_in_cents: plan.family_medical_deductible_in_network_in_cents,
    individual_medical_out_of_pocket_max_in_network_in_cents:
      plan.individual_medical_out_of_pocket_max_in_network_in_cents,
    family_medical_out_of_pocket_max_in_network_in_cents: plan.family_medical_out_of_pocket_max_in_network_in_cents,
    sbc_url: plan.sbc_url,
    benefits_summary_url: plan.benefits_summary_url,
    logo_url: plan.logo_url,
    carrier_logo_url: plan.carrier_logo_url,
    full_details: plan.details,
    off_market_in: plan.off_market_in,
    plan_start_date: plan.effective_date,
    plan_end_date: plan.expiration_date,
    desired_effective_date: coverageStartDate,
    effective_date: null, // Filled in later by agency, leave null
    application_completed_on: null, // Filled in later by agency, leave null
    application_id: null, // Filled in later by agency, leave null
    policy_active_confirmation_on: null, // Filled in later by agency, leave null
    policy_id: null, // Filled in later by agency, leave null
    autopay_confirmation_on: null, // Filled in later by agency, leave null
    payment_confirmation_id: null, // Filled in later by agency, leave null
    requires_attention_on: null // Filled in later by agency, leave null
  }
}
